import type {
  SDM,
  TemplateVersion,
  MeasurementUnit,
  ItemDef,
  MethodDef,
  ConditionDef,
  EventDef,
  FormDef,
  ItemGroupDef,
  CodeList,
} from '../types/sdm';

import type {
  SdmDocument,
  SdmTemplateVersion,
  SdmMeasurementUnit,
  SdmItemDef,
  SdmMethodDef,
  SdmConditionDef,
  SdmEventDef,
  SdmFormDef,
  SdmItemGroupDef,
  SdmCodeList,
  SdmRangeCheck,
} from '../types/sdm-wire';

export function serializeToSdmDocument(
  sdm: SDM,
  authorFirstName = 'GMS',
  authorLastName = 'User',
  tradeOid = '',
  savingDraft = false,
): SdmDocument {
  const now = new Date().toISOString();
  const toSerialize = sdm.TemplateVersion.filter(v => v.status !== 'draft');
  const versions = toSerialize.length > 0 ? toSerialize : sdm.TemplateVersion;
  const versionCount = versions.length;

  const draftVersion = sdm.TemplateVersion.find(v => v.status === 'draft');

  return {
    SDM: {
      Building: {
        GlobalVariables: {
          Name: sdm.GlobalVariables.Name,
          Description: sdm.GlobalVariables.Description ?? '',
          ProtocolName: sdm.GlobalVariables.Name,
        },
        TemplateVersion: versions.map((tv, i) =>
          serializeTemplateVersion(tv, i, sdm.GlobalVariables.Name),
        ),
        ...(savingDraft && draftVersion ? { _naomiDraft: serializeTemplateVersion(draftVersion, 0, sdm.GlobalVariables.Name) } : {}),
        BasicDefinition: {
          MeasurementUnit: sdm.BasicDefinition.MeasurementUnit.map(serializeMeasurementUnit),
        },
        OID: tradeOid,
      },
      xmlns: '',
      FileType: 'Snapshot',
      FileOID: sdm.GlobalVariables.Name || 'New Project',
      CreationDateTime: now,
      ODMVersion: '1.3.2',
      SourceSystem: '',
      AdminData: {
        User: { FirstName: authorFirstName, LastName: authorLastName, OID: 'U.1' },
        OID: tradeOid,
      },
      BuildingData: {
        EventData: [],
        Key: '123',
        OID: tradeOid,
        MetaDataVersionOID: `MDV.${versionCount > 0 ? versionCount : 1}`,
      },
    },
  };
}

function serializeMeasurementUnit(mu: MeasurementUnit): SdmMeasurementUnit {
  return {
    Name: mu.OID,
    OID: mu.OID,
    Symbol: { TranslatedText: { lang: 'en', text: mu.Symbol } },
  };
}

function serializeTemplateVersion(tv: TemplateVersion, index: number, tradeName?: string): SdmTemplateVersion {
  const allGroups = flattenGroups(tv.ItemGroupDef);
  const expandedFormDefs = tv.FormDef.map((f) => ({
    ...f,
    Name: tradeName || f.Name,
    ItemGroupRef: expandItemGroupRefs(f.ItemGroupRef, tv.ItemGroupDef),
  }));

  return {
    Protocol: {
      EventRef: tv.Protocol.EventRef.map((r) => ({
        EventOID: r.EventOID,
        Mandatory: (r as any).Mandatory ?? 'No',
      })),
    },
    Images: tv.Images ?? [],
    EventDef: tv.EventDef.map(serializeEventDef),
    FormDef: expandedFormDefs.map(serializeFormDef),
    ItemGroupDef: allGroups.map(serializeItemGroupDef),
    ItemDef: tv.ItemDef.map(serializeItemDef),
    MethodDef: tv.MethodDef.map(serializeMethodDef),
    CodeList: tv.CodeList.map(serializeCodeList),
    ConditionDef: tv.ConditionDef.map(serializeConditionDef),
    Mappings: (tv.Mappings ?? []).map(m => ({
      Name: m.Name,
      Source: m.Source,
      LastUpdated: m.LastUpdated ?? new Date().toISOString(),
      Description: m.Description ?? '',
      Versions: (m.Versions ?? []).map(v => ({
        VersionOID: v.VersionOID,
        VersionName: v.VersionName,
        CreatedBy: v.CreatedBy ?? 'Admin',
        CreatedDate: v.CreatedDate ?? new Date().toISOString(),
        ItemGroupDef: (v.ItemGroupDef ?? []).map(g => ({
          OID: g.OID,
          Name: g.Name,
          Repeating: g.Repeating === 'Yes' ? 'Yes' : 'No',
          Description: g.Description ?? { TranslatedText: { lang: 'en', text: g.Name } },
          ItemDef: g.ItemDef,
        })),
      })),
    })),
    OID: `MDV.${index + 1}`,
    Name: 'MetaDataVersion',
    ...(index > 0 ? { Include: { OID: `MDV.${index}` } } : {}),
    ...(tv._meta ? { _naomiMeta: tv._meta } : {}),
  };
}

function flattenGroups(groups: ItemGroupDef[], parentOid?: string): ItemGroupDef[] {
  const result: ItemGroupDef[] = [];
  for (const g of groups) {
    result.push(parentOid ? { ...g, _naomiParentOID: parentOid } as any : g);
    if (g.Subgroups?.length) {
      result.push(...flattenGroups(g.Subgroups, g.OID));
    }
  }
  return result;
}

function collectSubgroupRefs(groups: ItemGroupDef[]): Array<{ OID: string; MethodOID?: string; ConditionOID?: string; SummarizeInstances?: string }> {
  const result: Array<{ OID: string; MethodOID?: string; ConditionOID?: string; SummarizeInstances?: string }> = [];
  for (const g of groups) {
    result.push({
      OID: g.OID,
      ...(g.MethodOID    ? { MethodOID:    g.MethodOID }    : {}),
      ...(g.ConditionOID ? { ConditionOID: g.ConditionOID } : {}),
      ...((g as any).SummarizeInstances ? { SummarizeInstances: (g as any).SummarizeInstances } : {}),
    });
    if (g.Subgroups?.length) result.push(...collectSubgroupRefs(g.Subgroups));
  }
  return result;
}

function expandItemGroupRefs(refs: any[], groups: ItemGroupDef[]): any[] {
  const groupByOid = new Map(groups.map((g) => [g.OID, g]));
  const explicitOids = new Set(refs.map((r) => r.ItemGroupOID));

  const subgroupCondByOid = new Map<string, string | undefined>();
  for (const g of groups) {
    if (g.Subgroups?.length) {
      for (const sgRef of collectSubgroupRefs(g.Subgroups)) {
        subgroupCondByOid.set(sgRef.OID, sgRef.ConditionOID);
      }
    }
  }

  const result: any[] = [];
  for (const ref of refs) {
    if (subgroupCondByOid.has(ref.ItemGroupOID)) {
      const condOid = subgroupCondByOid.get(ref.ItemGroupOID);
      const serialized: any = { ...ref };
      if (condOid) {
        serialized.CollectionExceptionConditionOID = condOid;
      } else {
        delete serialized.CollectionExceptionConditionOID;
      }
      result.push(serialized);
    } else {
      result.push(ref);
    }
    const g = groupByOid.get(ref.ItemGroupOID);
    if (g?.Subgroups?.length) {
      for (const sgRef of collectSubgroupRefs(g.Subgroups)) {
        if (explicitOids.has(sgRef.OID)) continue; // already present with its full properties
        result.push({
          ItemGroupOID: sgRef.OID,
          Mandatory: 'No',
          ...(sgRef.MethodOID         ? { MethodOID: sgRef.MethodOID }                                    : {}),
          ...(sgRef.ConditionOID      ? { CollectionExceptionConditionOID: sgRef.ConditionOID }            : {}),
          ...(sgRef.SummarizeInstances ? { SummarizeInstances: sgRef.SummarizeInstances }                  : {}),
        });
      }
    }
  }
  return result;
}

function serializeEventDef(e: EventDef): SdmEventDef {
  return {
    Description: { TranslatedText: { lang: 'en', text: e.Name } },
    FormRef: e.FormRef.map((r) => ({
      FormOID: r.FormOID,
      Mandatory: (r as any).Mandatory ?? 'No',
    })),
    OID: e.OID,
    Name: e.OID,
    Repeating: 'No',
    Type: e.Type || 'Common',
  };
}

function serializeFormDef(f: FormDef): SdmFormDef {
  return {
    Description: { TranslatedText: { lang: 'en', text: f.Name } },
    ItemGroupRef: f.ItemGroupRef.map((ref) => ({ ...ref })) as any,
    OID: f.OID,
    Name: f.OID,
    Repeating: 'No',
  };
}

function serializeItemGroupDef(g: ItemGroupDef): SdmItemGroupDef {
  return {
    OID: g.OID,
    ItemRef: g.ItemRef.map((ref) => ({ ...ref })) as any,
    Description: { TranslatedText: { lang: 'en', text: g.Name } },
    Name: g.OID,
    Repeating: g.Repeating,
    ...((g as any)._naomiParentOID ? { _naomiParentOID: (g as any)._naomiParentOID } : {}),
    ...((g as any).RepeatFromOID   ? { _naomiRepeatFromOID: (g as any).RepeatFromOID } : {}),
    ...(g.MethodOID    ? { MethodOID:    g.MethodOID }    : {}),
    ...(g.ConditionOID ? { ConditionOID: g.ConditionOID } : {}),
  };
}

function serializeItemDef(item: ItemDef): SdmItemDef {
  const gmsItem: SdmItemDef = {
    Question: item.Question,
    OID: item.OID,
    Name: item.MetaKey || item.OID,
    DataType: item.DataType,
  };

  if (item.MeasurementOid) gmsItem.MeasurementOid = item.MeasurementOid;
  if (item.CodeListRef)    gmsItem.CodeListRef    = item.CodeListRef;
  if (item.MetaKey)        gmsItem.MetaKey        = item.MetaKey;
  if (item.ImageUrl)       gmsItem.ImageUrl       = item.ImageUrl;
  if (item.ThumbnailUrl)   gmsItem.ThumbnailUrl   = item.ThumbnailUrl;

  const anyItem = item as any;
  if (anyItem.AutoFillRef) gmsItem.AutoFillRef = anyItem.AutoFillRef;

  if (item.RangeCheck?.length) {
    gmsItem.RangeCheck = item.RangeCheck as SdmRangeCheck[];
  } else {
    const val = (item as any).Validations ?? (item as any).Validation;
    if (val?.min !== undefined || val?.max !== undefined) {
      const rangeChecks: SdmRangeCheck[] = [];
      if (val.min !== undefined) {
        rangeChecks.push({
          Comparator: 'LT',
          SoftHard: 'Hard',
          CheckValue: String(val.min),
          ErrorMessage: { TranslatedText: { lang: 'en', text: val.message ?? `Value must be >= ${val.min}` } },
        });
      }
      if (val.max !== undefined) {
        rangeChecks.push({
          Comparator: 'GT',
          SoftHard: 'Hard',
          CheckValue: String(val.max),
          ErrorMessage: { TranslatedText: { lang: 'en', text: val.message ?? `Value must be <= ${val.max}` } },
        });
      }
      gmsItem.RangeCheck = rangeChecks;
    }
  }

  return gmsItem;
}

function serializeMethodDef(m: MethodDef): SdmMethodDef {
  const gms: SdmMethodDef = {
    Description: { TranslatedText: { lang: 'en', text: m.humanReadable || m.OID } },
    FormalExpression: m.FormalExpression,
    OID: m.OID,
    Name: m.OID,
  };
  const anyM = m as any;
  if (anyM.MethodType) gms.MethodType = anyM.MethodType;
  return gms;
}

function serializeConditionDef(c: ConditionDef): SdmConditionDef {
  return {
    Description: { TranslatedText: { lang: 'en', text: c.humanReadable || c.OID } },
    FormalExpression: c.FormalExpression,
    OID: c.OID,
    Name: c.OID,
    ...(c.triggerOid !== undefined && { _naomi_triggerOid: c.triggerOid }),
    ...(c.op          !== undefined && { _naomi_op:         c.op }),
    ...(c.val         !== undefined && { _naomi_val:        c.val }),
    ...(c.expression  !== undefined && { _naomi_expression: c.expression }),
  };
}

function serializeCodeList(cl: CodeList): SdmCodeList {
  return {
    CodeListItem: cl.CodeListItem,
    OID: cl.OID,
    Name: cl.OID,
    DataType: cl.DataType,
  };
}
